import { AppText } from "@toothfairy/shared-ui";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View, useWindowDimensions } from "react-native";
import agentUtilsAPI from "../../../../../API/AgentUtils";
import ToothFairySubmitButton from "../../../../../Components/ToothFairyComponents/ToothFairySubmitButton";
import ToothFairyLoadingPlaceholder from "../../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../../Hooks/useWorkspaces";

const SpawnAgentModal = ({
  setIsSpawnAgentModalOpen,
  setShowPreselectionModal,
  onAgentGenerated,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const isLargerScreen = width > 768;

  const [isGenerating, setIsGenerating] = useState(false);

  const validationSchema = AppForms.yup.object().shape({
    instruction: AppForms.yup
      .string()
      .min(10, t("settings.list.instructionMinChars"))
      .max(2000, t("settings.list.instructionMaxChars"))
      .required(t("settings.list.describeAgentTask")),
  });

  const handleGenerate = async (values, { setSubmitting }) => {
    try {
      setIsGenerating(true);
      const workspace = getActiveWorkspace();
      const workspaceId = workspace?.id;
      const workspaceToken = workspace?.workspaceToken;

      if (!workspaceId) {
        AppToasty.show(t("settings.list.noWorkspaceFound"), { type: "danger" });
        return;
      }

      if (!workspaceToken) {
        AppToasty.show(t("settings.list.workspaceTokenNotFound"), { type: "danger" });
        return;
      }

      const response = await agentUtilsAPI.spawnAgent(
        workspaceId,
        workspaceToken,
        values.instruction,
      );

      if (response?.success && response?.agent) {
        const generatedAgent = response.agent;

        // Transform the AI-generated config to match the form structure
        const agentConfig = {
          label: generatedAgent.label,
          description: generatedAgent.description || "",
          mode: generatedAgent.mode || "retriever",
          interpolationString: generatedAgent.interpolationString || "",
          goals: generatedAgent.goals || "",
          hasCode: generatedAgent.hasCode || false,
          agenticRAG: generatedAgent.agenticRAG || false,
          allowInternetSearch: generatedAgent.allowInternetSearch || false,
          allowDeepInternetSearch:
            generatedAgent.allowDeepInternetSearch || false,
          charting: generatedAgent.charting || false,
          allowImagesGeneration: generatedAgent.allowImagesGeneration || false,
          allowVideosGeneration: generatedAgent.allowVideosGeneration || false,
          allow3dModelGeneration:
            generatedAgent.allow3dModelGeneration || false,
          allowDocsUpload: generatedAgent.allowDocsUpload ?? true,
          allowImagesUpload: generatedAgent.allowImagesUpload ?? true,
          allowAudiosUpload: generatedAgent.allowAudiosUpload || false,
          allowVideosUpload: generatedAgent.allowVideosUpload || false,
          llmBaseModel: generatedAgent.llmBaseModel || "mystica",
          maxTokens: generatedAgent.maxTokens || 4096,
          temperature: generatedAgent.temperature ?? 0.1,
          minRetrievalScore: generatedAgent.minRetrievalScore || 0.6,
          department: generatedAgent.department || "INFORMATION_TECHNOLOGY",
          minimumSubscriptionType:
            generatedAgent.minimumSubscriptionType || "base",
          blender: generatedAgent.blender || false,
          hasImages: generatedAgent.hasImages || false,
          hasNER: generatedAgent.hasNER || false,
          hasModeration: generatedAgent.hasModeration || false,
          showCodeInExecution: generatedAgent.showCodeInExecution || false,
          allowedTopics: generatedAgent.allowedTopics || [],
          agentFunctions: generatedAgent.agentFunctions || [],
          customToolingInstructions:
            generatedAgent.customToolingInstructions || "",
        };

        AppToasty.show(t("settings.list.agentConfigGenerated"), {
          type: "success",
        });

        // Close modals and pass config to parent
        setIsSpawnAgentModalOpen(false);
        setShowPreselectionModal(false);

        if (onAgentGenerated) {
          onAgentGenerated(agentConfig);
        }
      } else {
        AppToasty.show(t("settings.list.agentConfigGenerationFailed"), {
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error spawning agent:", error);
      AppToasty.show(
        error?.message || t("settings.list.agentGenerationFailed"),
        { type: "danger" },
      );
    } finally {
      setIsGenerating(false);
      if (typeof setSubmitting === "function") {
        setSubmitting(false);
      }
    }
  };

  if (isGenerating) {
    return (
      <ToothFairyLoadingPlaceholder
        textStyle={{
          textAlign: "center",
        }}
        style={{
          position: "relative",
          height: 300,
        }}
        caption={t("settings.list.tfAnalysingRequirements")}
      />
    );
  }

  return (
    <ScrollView
      style={{
        minHeight: 400,
        maxHeight: isLargerScreen ? 600 : 500,
        width: "100%",
      }}
      contentContainerStyle={{
        padding: 16,
        minHeight: 400,
      }}
    >
      <View>
        <View style={{ alignItems: "center", marginBottom: 24 }}>
          <AppText
            preventTranslation
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: currentTheme?.theme?.font_color,
              textAlign: "center",
              maxWidth: 400,
              lineHeight: 20,
            }}
          >
            {t("settings.list.describeTaskPrompt")}
          </AppText>
        </View>

        <AppForms.AppForm
          initialValues={{ instruction: "" }}
          onSubmit={handleGenerate}
          validationSchema={validationSchema}
        >
          <View style={{ marginBottom: 16 }}>
            <AppForms.AppFormField
              name="instruction"
              label={t("settings.list.agentTaskLabel")}
              placeholder={t("settings.list.agentTaskPlaceholder")}
              multiline
              maxLength={2000}
              textContainer={{
                minHeight: 200,
                paddingTop: 10,
                textAlignVertical: "top",
              }}
            />
          </View>

          <View
            style={{
              backgroundColor: currentTheme?.theme?.softBackgroundColor,
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              marginHorizontal: 10,
            }}
          >
            <AppText
              style={{
                fontSize: 12,
                color: currentTheme?.theme?.secondary_font_color,
                lineHeight: 20,
              }}
              wrapperStyle={{
                marginHorizontal: 10,
              }}
              preventTranslation
            >
              {t("settings.list.tipsBestResults")}
              {"\n"}{t("settings.list.tipClearlyDescribe")}
              {"\n"}{t("settings.list.tipSpecifyCapabilities")}
              {"\n"}{t("settings.list.tipDefineOutput")}
            </AppText>
          </View>

          <ToothFairySubmitButton
            title={t("settings.list.generateAgent")}
            wrapperStyle={{
              alignSelf: "center",
              maxWidth: 300,
              minWidth: 200,
            }}
            awesomeIconName="magic"
          />
        </AppForms.AppForm>
      </View>
    </ScrollView>
  );
};

export default SpawnAgentModal;
